import React from 'react';
import BayesianNetworkMxGraph, {BayesianNodeStateJSON}
  from "../../../../../mxGraph/HCLTreeMxGraph/BayesianNetworkMxGraph";
import BayesianNetworkVertex from "../../../../../mxGraph/HCLTreeNode/BayesianNetworkNode/BayesianNetworkVertex";
import BayesianNetworkVertexValue
  from "../../../../../mxGraph/HCLTreeNode/BayesianNetworkNode/BayesianNetworkVertexValue";
import Expression from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/Expression";
import ProxyTypes from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ProxyTypes";
import Outcome from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/Outcome";
import ReferenceTypes from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ReferenceTypes";
import FunctionalEventVertex
  from "../../../../../mxGraph/HCLTreeNode/EventTreeNode/FunctionalEventNode/FunctionalEventVertex";
import EventTreeSuccessEdge from "../../../../../mxGraph/HCLTreeNode/EventTreeNode/EventTreeSuccessEdge";
import EventTreeFailureEdge from "../../../../../mxGraph/HCLTreeNode/EventTreeNode/EventTreeFailureEdge";
import HCLTreeMxGraph from "../../../../../mxGraph/HCLTreeMxGraph/HCLTreeMxGraph";
import HCLTreeVertex from "../../../../../mxGraph/HCLTreeNode/HCLTreeVertex";
import {Configurations} from "./Quantification";
import {mxCell} from "mxgraph-js";

const defaultConfig = {
  configurations: {},
  // @ts-ignore
  currentlySelected: null,
};

const resetConfigurations = () => {
  localStorage.setItem('configurations', JSON.stringify(defaultConfig));
};

export const updateConfigurations = (name: string | number, config: Configurations) => {
  let toUpdate = JSON.parse(localStorage.getItem('configurations'));
  if (!toUpdate) {
    toUpdate = {};
    toUpdate.configurations = {};
  }
  toUpdate.currentlySelected = name;
  toUpdate.configurations[name] = config;
  localStorage.setItem('configurations', JSON.stringify(toUpdate));
};

export const updateCurrentlySelected = (name: string) => {
  let toUpdate = JSON.parse(localStorage.getItem('configurations'));
  if (!toUpdate) {
    toUpdate = {};
    toUpdate.configurations = {};
    toUpdate.configurations[name] = {};
  }
  toUpdate.currentlySelected = name;
  localStorage.setItem('configurations', JSON.stringify(toUpdate));
};

export const getConfigurations = () => {
  const config = JSON.parse(localStorage.getItem('configurations'));
  if (!config) {
    resetConfigurations();
    return defaultConfig;
  }
  return config;
};

export const deleteConfiguration = (e: React.MouseEvent<HTMLElement>, configName: any) => {
  e.stopPropagation();
  const configs = getConfigurations();
  Object.keys(configs.configurations).forEach((key: any) => {
    if (key === configName) {
      delete configs.configurations[key];
    }
  });
  configs.currentlySelected = null;
  localStorage.setItem('configurations', JSON.stringify(configs));
};

/**
 * return transpose of @param {*[]}
 * @param {*[][]} matrix - 2D array
 * @return {*[][]} 2D array
 *
 * @example
 * // returns [[1, 1], [2, 2], [3, 3]]
 * const matrix = [[1, 2, 3], [1, 2, 3]];
 * transpose(matrix);
 */
export const transpose = (matrix: (string | number)[][]) => matrix[0].map((col, i) => matrix.map(row => row[i]));

/**
 * return table data to display
 *
 * @param {BayesianNetworkMxGraph} graph
 * @param {BayesianNetworkVertex} targetCell
 *
 * @return {[Number, Number, string[], string[], *[][]]} An array of objects
 * Index 0: number of rows of the table data (exclude header)
 * Index 1: number of columns of the table data (exclude header)
 * Index 2: table header labels of dependent cells
 * Index 3: table header labels of target cell
 * Index 4: table data (exclude header, transposed)
 *
 * @example
 * // returns [
 * //   4,
 * //   4,
 * //   ["Thermal_Runaway", "Lithium_Plating"],
 * //   ["T", "F"],
 * //   [
 * //      ["T", "T", "F", "F"],
 * //      ["T", "F", "T", "F"],
 * //      [0.99, 0.9, 0.9, 0],
 * //      [0.01, 0.1, 0.1, 1]
 * //   ]
 * // ]
 * generateTableData(graph, targetCell)
 */
export const generateTableData = (graph: BayesianNetworkMxGraph, targetCell: BayesianNetworkVertex): [number, number, BayesianNetworkVertex[], string[], (string | number)[][]] => {
  const targetCellIncomingEdges = graph.getModel().getIncomingEdges(targetCell);
  const targetCellData = targetCell.getValue() as BayesianNetworkVertexValue;

  // array of states of dependent cells
  const statesArray = targetCellIncomingEdges.map(edge => Object.keys((edge.source.getValue() as BayesianNetworkVertexValue).getStates()));

  /** compute the number of rows and columns **/
  const numDependentCols = targetCellIncomingEdges.length;
  let numRows = 1;
  // number of rows is multiple of the length of the states of dependent cell
  statesArray.forEach((states) => numRows *= states.length);

  /** Filling labels of table data **/
  const matrix: (string | number)[][] = [];
  statesArray.forEach((states, index) => {
    const arr = [];
    /**
     * number of repeat is multiple of the length of the states to the right of current state
     * @example
     * statesArray = [
     *     ["Raining", "Not Raining"],
     *     ["0%", "50%", "100%"],
     *     ["No Clouds", "Some Clouds", "Many Clouds"]
     * ]
     *
     * numRepeat for states in index 0 is 9
     * numRepeat for states in index 1 is 3
     * numRepeat for states in index 2 is 1 (initial value)
     */
    const numRepeat = statesArray.slice(index + 1).map(states => states.length).reduce((x, y) => x * y, 1);
    for (let i = 0; i < numRows; i++) {
      const newIndex = Math.floor(Math.floor(i / numRepeat) % states.length);
      arr.push(states[newIndex]);
    }
    matrix.push(arr);
  });

  //Filling probabilities of table data
  Object.values(targetCellData.getStates()).map(value => {
    const probabilityArr = value.probabilities.map(probabilityJSON => {
      return probabilityJSON.expression.value;
    });
    matrix.push(probabilityArr);
  });

  const parents = targetCellIncomingEdges.map(edge => (edge.source as BayesianNetworkVertex));
  const targetStateNames = Object.keys(targetCellData.getStates());
  const numCols = numDependentCols + targetStateNames.length;

  return [numRows, numCols, parents, targetStateNames, matrix];
};

export const extractStates = (transposedMatrix: (string | number)[][], parents: BayesianNetworkVertex[], targetStateNames: string[], numRows: number, graphModelId: number) => {
  const states: {[key: string]: BayesianNodeStateJSON} = {};
  targetStateNames.forEach((stateName, stateCol) => {
    states[stateName] = {
      probabilities: new Array(numRows).fill(0).map((_, row) => ({
        expression: new Expression({
          value: Number(transposedMatrix[row][parents.length + stateCol]),
          _proxy: ProxyTypes.FLOAT
        }).toJSON(),

        states: new Array(parents.length).fill(0).map((_, dependentCol) =>
          new Outcome(
            transposedMatrix[row][dependentCol] as string,
            ReferenceTypes.STATES,
            graphModelId,
            `bayesian_nodes.${(parents[dependentCol]).getKey()}`
          ).toJSON()
        )
      }))
    }
  });
  return states;
};

export const addClonedCells = (cells: any, graph: HCLTreeMxGraph) => {
  const copyCell: { [index: string]: HCLTreeVertex } = {};
  if(!cells || !cells.length) {
    return;
  }
  const edges: mxCell[] = [];
  const model = graph.getModel();
  try {
    model.beginUpdate();
    cells.forEach((cell: mxCell) => {
      if (!cell.isEdge()) {
        const clonedCell = graph.addVertexWithUniqueKey(cell.clone(), null, false);
        copyCell[cell.id] = clonedCell;
        if (clonedCell instanceof FunctionalEventVertex) {
          copyCell[(cell as FunctionalEventVertex).successPort.id] = clonedCell.successPort;
          copyCell[(cell as FunctionalEventVertex).failurePort.id] = clonedCell.failurePort;
        }
      } else {
        edges.push(cell);
      }
    });
    edges.forEach((cell: mxCell) => {
      if (copyCell[cell.source.id] && copyCell[cell.target.id]) {
        if (cell instanceof EventTreeSuccessEdge) {
          graph.addEdge(new EventTreeSuccessEdge(), graph.getDefaultParent(), copyCell[cell.source.id], copyCell[cell.target.id]);
        } else if (cell instanceof EventTreeFailureEdge) {
          graph.addEdge(new EventTreeFailureEdge(), graph.getDefaultParent(), copyCell[cell.source.id], copyCell[cell.target.id]);
        } else {
          graph.insertEdge(graph.getDefaultParent(), null, null, copyCell[cell.source.id], copyCell[cell.target.id], null)
        }
      }
    });
  } catch (e) {
    console.error(e);
  } finally {
    graph.applyDefaultLayout();
    graph.setSelectionCells(Object.values(copyCell));
    model.endUpdate();
  }
};
